import React, {ReactNode} from 'react';

import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {IconName} from '../../../libs/shared/ui/src/components/icon/icon';
import {openInBrowser} from '../../../srcCommon/browser/openInBrowser';
import startGuideArticleArchive from '../../assets/images/startGuideArticleArchive.png';
import startGuideArticleAssign from '../../assets/images/startGuideArticleAssign.png';
import startGuideArticleComment from '../../assets/images/startGuideArticleComment.png';
import startGuideArticleInbox from '../../assets/images/startGuideArticleInbox.png';
import startGuideArticleRocket from '../../assets/images/startGuideArticleRocket.png';
import startGuideArticleSettings from '../../assets/images/startGuideArticleSettings.png';
import startGuideArticleTag from '../../assets/images/startGuideArticleTag.png';
import startGuideArticleTeam from '../../assets/images/startGuideArticleTeam.png';

/*
 * Types.
 */

export type StartGuideArticleDefinition = StartGuideArticleLegacyDefinition | StartGuideArticleIconDefinition;

/*
 * Const.
 */

export enum StartGuideArticleTypesEnum {
  PNG = 'png',
  COMPONENT = 'component',
}

export const StartGuideArticleThumbnails = {
  COMMENT: startGuideArticleComment,
  ASSIGN: startGuideArticleAssign,
  ARCHIVE: startGuideArticleArchive,
  TAG: startGuideArticleTag,
  INBOX: startGuideArticleInbox,
  TEAM: startGuideArticleTeam,
  SETTINGS: startGuideArticleSettings,
  ROCKET: startGuideArticleRocket,
};
export interface StartGuideArticleBaseDefinition {
  id: string;
  order: number;
  title: ReactNode;
  description: ReactNode;
  onClick: () => void;
}
export interface StartGuideArticleLegacyDefinition extends StartGuideArticleBaseDefinition {
  type: StartGuideArticleTypesEnum.PNG;
  thumbnail: string;
}

export interface StartGuideArticleIconDefinition extends StartGuideArticleBaseDefinition {
  type: StartGuideArticleTypesEnum.COMPONENT;
  iconName: IconName;
  outerBackgroundColor: string;
  innerBackgroundColor: string;
}

export const startGuideUserArticles: Array<StartGuideArticleLegacyDefinition> = [
  {
    type: StartGuideArticleTypesEnum.PNG,
    id: 'organize_with_tags',
    order: 1,
    thumbnail: StartGuideArticleThumbnails.TAG,
    title: <Trans id="start-guide-article-tagtitle">Prioritize your work with tags</Trans>,
    description: (
      <Trans id="start-guide-article-tagdescription">
        Private tags can be used as your personal folders. For a shortcut to messages with important tags, pin
        them to your private workspace. You can then drag and drop conversations from your Inbox to your
        private tags to file them away, or add private tags using the tag icon to mark and categorize them.
      </Trans>
    ),
    onClick: () => openInBrowser('https://help.front.com/t/m2nqjq#how-to-pin-a-tag-to-your-workspace'),
  },
  {
    type: StartGuideArticleTypesEnum.PNG,
    id: 'avoid_messy_email',
    order: 2,
    thumbnail: StartGuideArticleThumbnails.COMMENT,
    title: <Trans id="start-guide-article-commenttitle">Comment on emails instead of forwarding</Trans>,
    description: (
      <Trans id="start-guide-article-commentdescription">
        Use comments to work with your teammates and mention them to collaborate internally. Comments are
        internal messages you can post on any conversation, using the comment bar at the bottom of any
        conversation. Only your team can see posted comments, so you can discuss the conversation amongst
        yourselves without having to forward the message around. You can also get a specific teammate&apos;s
        attention using @mentions — just type the @ symbol in the comment bar, then their name. That will pop
        the conversation into that teammate&apos;s inbox in Shared with me for them to comment. The cherry on
        top is that you can use emojis to react to comments!
      </Trans>
    ),
    onClick: () => openInBrowser('https://help.front.com/t/k924v1/understanding-comments'),
  },
  {
    type: StartGuideArticleTypesEnum.PNG,
    id: 'assign_convo',
    order: 3,
    thumbnail: StartGuideArticleThumbnails.ASSIGN,
    title: <Trans id="start-guide-article-assigntitle">Assign conversations</Trans>,
    description: (
      <Trans id="start-guide-article-assigndescription">
        Give every message a clear owner and ensure nothing falls through the cracks. You&apos;ll typically
        assign a conversation to the person on your team in charge of replying to that specific inquiry. By
        assigning all conversations, you can ensure no message goes unreplied. A conversation can only be
        assigned to one user at a time.
      </Trans>
    ),
    onClick: () => openInBrowser('https://help.front.com/t/y724vf/how-to-assign-a-conversation#overview'),
  },
  {
    type: StartGuideArticleTypesEnum.PNG,
    id: 'archive_inbox_zero',
    order: 4,
    thumbnail: StartGuideArticleThumbnails.ARCHIVE,
    title: <Trans id="start-guide-article-archivetitle">Archive to hit Inbox Zero</Trans>,
    description: (
      <Trans id="start-guide-article-archivedescription">
        Think of archiving your emails as checking off your or your team&apos;s to-do list, and the goal is to
        check off all your tasks and thus reach inbox zero. Archiving will keep the conversation in your
        inbox, but will move it to the Archived tab of the inbox. Once you&apos;ve read or actioned on the
        conversation, you should archive it or snooze it to reappear later.
      </Trans>
    ),
    onClick: () => openInBrowser('https://help.front.com/t/8024vn/how-to-archive-a-conversation#why-archive'),
  },
  {
    type: StartGuideArticleTypesEnum.PNG,
    id: 'archived_vs_read',
    order: 5,
    thumbnail: StartGuideArticleThumbnails.INBOX,
    title: <Trans id="start-guide-article-inboxtitle">Open/Archived vs. Read/Unread</Trans>,
    description: (
      <Trans id="start-guide-article-inboxdescription">
        Archiving instead of marking emails as read is the Front-recommended way to manage your inbox. Once no
        immediate action is required on a message, simply click on the Archive icon at the top of the app.
        This way, you&apos;ll be able to focus on only seeing the conversations that need your attention,
        rather than having to look through a long list of both Read and Unread conversations. Conversations
        will automatically unarchive and appear in your Inbox when there&apos;s a new message or comment.
      </Trans>
    ),
    onClick: () => openInBrowser('https://help.front.com/t/m224v6#mark-as-read-vs-archiving'),
  },
];

export const startGuideAdminArticles: Array<StartGuideArticleLegacyDefinition> = [
  {
    type: StartGuideArticleTypesEnum.PNG,
    id: 'archived_vs_read',
    order: 1,
    thumbnail: StartGuideArticleThumbnails.INBOX,
    title: <Trans id="start-guide-article-inboxtitle">Open/Archived vs. Read/Unread</Trans>,
    description: (
      <Trans id="start-guide-article-inboxdescription">
        Archiving instead of marking emails as read is the Front-recommended way to manage your inbox. Once no
        immediate action is required on a message, simply click on the Archive icon at the top of the app.
        This way, you&apos;ll be able to focus on only seeing the conversations that need your attention,
        rather than having to look through a long list of both Read and Unread conversations. Conversations
        will automatically unarchive and appear in your Inbox when there&apos;s a new message or comment.
      </Trans>
    ),
    onClick: () => openInBrowser('https://help.front.com/t/m224v6#mark-as-read-vs-archiving'),
  },
  {
    type: StartGuideArticleTypesEnum.PNG,
    id: 'tags_rules_more',
    order: 2,
    thumbnail: StartGuideArticleThumbnails.TEAM,
    title: <Trans id="start-guide-article-teamtitle">Share tags, signatures, and more with your team</Trans>,
    description: (
      <Trans id="start-guide-article-teamdescription">
        Shared tags, signatures, canned responses (i.e. a saved message template), and rules are essential
        tools to improve your team’s efficiency and your customer’s experience. One of the powers of Front is
        that once you create these shared resources, they can be easily used and updated for your entire team.
        That means no more manual and tedious maintenance of siloed and spread out information that can result
        in your customer communications being out-of-date or slow — you can easily update then share these
        team features to ensure the best experience for your customers.
      </Trans>
    ),
    onClick: () =>
      openInBrowser('https://help.front.com/t/m1h38y3/the-basics-setting-up-your-teams-workflow#overview'),
  },
  {
    type: StartGuideArticleTypesEnum.PNG,
    id: 'manage_team_settings',
    order: 3,
    thumbnail: StartGuideArticleThumbnails.SETTINGS,
    title: <Trans id="start-guide-article-settingstitle">Manage your team&apos;s settings</Trans>,
    description: (
      <Trans id="start-guide-article-settingsdescription">
        Customize your teammates&apos; settings so they can easily transition into Front. Front admins have
        access to manage their team members&apos; preferences, signatures, and shared inbox permissions. This
        allows you to standardize certain settings like a default reply channel or canned response shortcuts
        for everyone on your team. You can easily configure settings for new team members or update them along
        the way, without requiring them to learn and edit each one themselves.
      </Trans>
    ),
    onClick: () =>
      openInBrowser('https://help.front.com/t/36b5xn/how-admins-can-manage-teammate-settings#overview'),
  },
  {
    type: StartGuideArticleTypesEnum.PNG,
    id: 'change_management_tools',
    order: 4,
    thumbnail: StartGuideArticleThumbnails.ROCKET,
    title: <Trans id="start-guide-article-rockettitle">Manage change for your team with ease</Trans>,
    description: (
      <Trans id="start-guide-article-rocketdescription">
        Ready to invite your team into Front? Follow this checklist to make sure everything is ready for your
        team to jump in and hit the ground running. We know change can be hard, but following these steps will
        set your team up for success!
      </Trans>
    ),
    onClick: () =>
      openInBrowser('https://help.front.com/t/q6hywqp/a-guide-for-transitioning-to-front#overview'),
  },
];
